import Icon from 'components/Icon';
import {IconType} from 'components/Icon/types';
import {useTooltip} from 'components/Tooltip/hook';
import {FC} from 'react';
import {twMerge} from 'tailwind-merge';
import {truncateLabel} from 'utils/functions';

export type PillColor = `${PillPrimaryColor}_dark` | PillPrimaryColor;

type PillPrimaryColor =
    | 'blue'
    | 'coral'
    | 'gold'
    | 'green'
    | 'grey'
    | 'lime'
    | 'orange'
    | 'pink';

const PillColors: Record<PillColor, string> = {
    blue: 'bg-blue-100 text-blue-700',
    blue_dark: 'bg-blue-700 text-white',
    coral: 'bg-coral-100 text-coral-800',
    coral_dark: 'bg-coral-700 text-white',
    gold: 'bg-gold-100 text-gold-900',
    gold_dark: 'bg-gold-800 text-white',
    green: 'bg-green-100 text-green-800',
    green_dark: 'bg-green-700 text-white',
    grey: 'bg-grey-200 text-grey-700',
    grey_dark: 'bg-grey-700 text-white',
    lime: 'bg-decorative-lime-green-100 text-decorative-lime-green-800',
    lime_dark: 'bg-decorative-lime-green-800 text-white',
    orange: 'bg-orange-100 text-orange-700',
    orange_dark: 'bg-orange-700 text-white',
    pink: 'bg-decorative-sakura-pink-100 text-decorative-sakura-pink-800 ',
    pink_dark: 'bg-decorative-sakura-pink-800 text-white',
};

export const isValidPillColor = (color?: string): color is PillColor => {
    if (!color) return false;

    return Object.keys(PillColors).includes(color);
};

type PillProps = {
    className?: string;
    color: PillColor;
    icon?: IconType;
    isTruncated?: boolean;
    label: string;
    maxLength?: number;
};

const Pill: FC<PillProps> = ({
    className,
    color,
    icon,
    isTruncated = true,
    label,
    maxLength,
}) => {
    const {setTooltipRef} = useTooltip();

    return (
        <div
            className={twMerge(
                'flex gap-1 items-center rounded-full ',
                'whitespace-nowrap text-sm font-semibold',
                'w-fit py-0.5 px-2',
                PillColors[color],
                className,
            )}
            ref={(ref) => setTooltipRef(ref)}
            role="complementary"
        >
            {icon && <Icon icon={icon} size="base" />}
            <div>{isTruncated ? truncateLabel(label, maxLength) : label}</div>
        </div>
    );
};
export default Pill;
